import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Server, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { IntegrationIcon } from '../components/IntegrationIcon';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../api/client';

interface IntegrationEntry {
  name: string;
  href: string;
  // IntegrationIcon catalog key for the colored brand icon…
  iconKey?: string;
  // …or a lucide fallback for entries without one (e.g. On-Prem Deployment).
  fallbackIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  // getWorkflowIntegrationsStatus ids that mark this entry connected (any-of).
  statusIds?: string[];
  onPremOnly?: boolean;
}

interface IntegrationCategory {
  label: string;
  entries: IntegrationEntry[];
}

// Grouped like the landing page's "Connects to your entire stack" grid so the
// two surfaces stay familiar. Single-integration categories go last so the
// grid doesn't strand near-empty cards mid-page.
const CATEGORIES: IntegrationCategory[] = [
  {
    label: 'Infrastructure',
    entries: [
      { name: 'Kubernetes', href: '/onboard-cluster', iconKey: 'kubernetes', statusIds: ['kubernetes'] },
      { name: 'AWS', href: '/cloud-integrations', iconKey: 'aws', statusIds: ['aws'] },
      { name: 'GCP', href: '/cloud-integrations', iconKey: 'gcp', statusIds: ['gcp'] },
      { name: 'Oracle Cloud (OCI)', href: '/cloud-integrations', iconKey: 'oci', statusIds: ['oci'] },
      { name: 'Karpenter', href: '/integrations/karpenter', iconKey: 'karpenter', statusIds: ['karpenter'] },
    ],
  },
  {
    label: 'PaaS',
    entries: [
      { name: 'Vercel', href: '/integrations/vercel', iconKey: 'vercel', statusIds: ['vercel'] },
      { name: 'Railway', href: '/integrations/railway', iconKey: 'railway', statusIds: ['railway'] },
      { name: 'Fly.io', href: '/integrations/flyio', iconKey: 'flyio', statusIds: ['flyio'] },
    ],
  },
  {
    label: 'AI Compute',
    entries: [
      { name: 'Nebius', href: '/integrations/nebius', iconKey: 'nebius', statusIds: ['nebius'] },
      { name: 'Beam', href: '/integrations/beam', iconKey: 'beam', statusIds: ['beam'] },
      { name: 'Daytona', href: '/integrations/daytona', iconKey: 'daytona', statusIds: ['daytona'] },
    ],
  },
  {
    label: 'Observability',
    entries: [
      { name: 'Datadog', href: '/integrations/datadog', iconKey: 'datadog', statusIds: ['datadog'] },
      { name: 'PostHog', href: '/integrations/posthog', iconKey: 'posthog', statusIds: ['posthog'] },
    ],
  },
  {
    label: 'On-call',
    entries: [
      { name: 'Slack', href: '/integrations/slack', iconKey: 'slack', statusIds: ['slack'] },
      { name: 'PagerDuty', href: '/integrations/pagerduty', iconKey: 'pagerduty', statusIds: ['pagerduty'] },
    ],
  },
  {
    label: 'Secrets',
    entries: [
      { name: 'HashiCorp Vault', href: '/integrations/vault', iconKey: 'vault', statusIds: ['vault'] },
      { name: 'Infisical', href: '/integrations/infisical', iconKey: 'infisical', statusIds: ['infisical'] },
    ],
  },
  {
    label: 'Security',
    entries: [
      { name: 'SonarCloud', href: '/integrations/sonarcloud', iconKey: 'sonarcloud', statusIds: ['sonarcloud'] },
      { name: 'Okta', href: '/integrations/okta', iconKey: 'okta', statusIds: ['okta'] },
      { name: 'Kyverno', href: '/integrations/kyverno', iconKey: 'kyverno', statusIds: ['kyverno'] },
      { name: 'Trivy', href: '/integrations/trivy', iconKey: 'trivy', statusIds: ['trivy'] },
    ],
  },
  {
    label: 'CI/CD',
    entries: [
      { name: 'GitHub', href: '/cicd-integration', iconKey: 'github', statusIds: ['github'] },
      { name: 'GitLab', href: '/cicd-integration', iconKey: 'gitlab', statusIds: ['gitlab'] },
      { name: 'Jenkins', href: '/integrations/jenkins', iconKey: 'jenkins', statusIds: ['jenkins'] },
      { name: 'CircleCI', href: '/integrations/circleci', iconKey: 'circleci', statusIds: ['circleci'] },
    ],
  },
  {
    label: 'Databases',
    entries: [
      { name: 'Supabase', href: '/integrations/supabase', iconKey: 'supabase', statusIds: ['supabase'] },
      { name: 'PlanetScale', href: '/integrations/planetscale', iconKey: 'planetscale', statusIds: ['planetscale'] },
      { name: 'Neon', href: '/integrations/neon', iconKey: 'neon', statusIds: ['neon'] },
      { name: 'ClickHouse', href: '/integrations/clickhouse', iconKey: 'clickhouse', statusIds: ['clickhouse'] },
    ],
  },
  {
    label: 'IaC & GitOps',
    entries: [
      { name: 'Terraform Cloud', href: '/integrations/terraform', iconKey: 'terraform', statusIds: ['terraform'] },
      { name: 'Pulumi Cloud', href: '/integrations/pulumi', iconKey: 'pulumi', statusIds: ['pulumi'] },
      { name: 'Argo CD', href: '/integrations/argocd', iconKey: 'argocd', statusIds: ['argocd'] },
      { name: 'Argo Rollouts', href: '/integrations/argo-rollouts', iconKey: 'argo-rollouts', statusIds: ['argo-rollouts'] },
      { name: 'Flux CD', href: '/integrations/fluxcd', iconKey: 'fluxcd', statusIds: ['fluxcd'] },
    ],
  },
  {
    // Knowledge sources share one setup page but are distinct connections, so
    // each brand gets its own entry.
    label: 'Knowledge',
    entries: [
      { name: 'Confluence', href: '/integrations/knowledge-sources', iconKey: 'confluence', statusIds: ['confluence'] },
      { name: 'Jira', href: '/integrations/knowledge-sources', iconKey: 'jira', statusIds: ['jira'] },
      { name: 'Linear', href: '/integrations/knowledge-sources', iconKey: 'linear', statusIds: ['linear'] },
      { name: 'Notion', href: '/integrations/knowledge-sources', iconKey: 'notion', statusIds: ['notion'] },
      { name: 'Glean', href: '/integrations/knowledge-sources', iconKey: 'glean', statusIds: ['glean'] },
      { name: 'On-Prem Deployment', href: '/on-prem', fallbackIcon: Server, onPremOnly: true },
    ],
  },
  {
    label: 'Networking',
    entries: [
      { name: 'Cloudflare', href: '/integrations/cloudflare', iconKey: 'cloudflare', statusIds: ['cloudflare'] },
    ],
  },
  {
    label: 'Data Platform',
    entries: [
      { name: 'Databricks', href: '/integrations/databricks', iconKey: 'databricks', statusIds: ['databricks'] },
    ],
  },
];


export default function IntegrationsIndex() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [connected, setConnected] = useState<Set<string>>(new Set());

  useEffect(() => {
    apiClient
      .getWorkflowIntegrationsStatus()
      .then(statuses => {
        setConnected(new Set(statuses.filter(s => s.connected).map(s => s.id)));
      })
      .catch(() => {});
  }, []);

  const lowerSearch = search.trim().toLowerCase();
  const categories = useMemo(() => {
    return CATEGORIES.map(cat => ({
      ...cat,
      entries: cat.entries.filter(e => {
        if (e.onPremOnly && !user?.is_onprem_enabled) return false;
        if (!lowerSearch) return true;
        return e.name.toLowerCase().includes(lowerSearch) || cat.label.toLowerCase().includes(lowerSearch);
      }),
    })).filter(cat => cat.entries.length > 0);
  }, [lowerSearch, user?.is_onprem_enabled]);

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search integrations..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {categories.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No integrations match "{search}".
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(cat => (
            <Card key={cat.label} className="overflow-hidden">
              <CardContent className="p-4">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                  {cat.label}
                </p>
                <div className="-mx-2">
                  {cat.entries.map(entry => {
                    const isConnected = (entry.statusIds || []).some(id => connected.has(id));
                    return (
                      <Link
                        key={entry.name}
                        to={entry.href}
                        className="group flex items-center gap-3 px-2 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <span className="flex h-5 w-5 items-center justify-center flex-shrink-0">
                          {entry.iconKey ? (
                            <IntegrationIcon integration={entry.iconKey} size={18} />
                          ) : entry.fallbackIcon ? (
                            <entry.fallbackIcon className="h-[18px] w-[18px] text-muted-foreground group-hover:text-accent-foreground" aria-hidden="true" />
                          ) : null}
                        </span>
                        <span className="flex-1 truncate font-medium">{entry.name}</span>
                        {isConnected && (
                          <span className="flex items-center gap-1.5 text-[11px] text-emerald-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Connected
                          </span>
                        )}
                        <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0" />
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
